import logoLarge from '../assets/logo-placeholder.svg?url';
import featuredDishPlaceholder from '../assets/featured-dish-placeholder.svg?url';
import restaurantBg from '../assets/restaurant-bg.svg?url';

export class WelcomeScreen {
  constructor(config = null) {
    this.config = config || this.getDefaultConfig();
    this.specialDishes = this.getDefaultSpecials();
    this.hasMenu = true;

    const menuSpecials = this.config?.menu?.specials;
    if (Array.isArray(menuSpecials) && menuSpecials.length > 0) {
      this.specialDishes = menuSpecials.map((special) => ({
        ...special,
        price: typeof special.price === 'number' ? special.price : Number(special.price ?? 0),
        currency: special.currency || this.config?.settings?.currency || 'PLN',
        currencySymbol: special.currencySymbol || null,
      }));
    }
  }

  getDefaultConfig() {
    return {
      name: 'Restaurant',
      tagline: 'Witamy w naszej restauracji',
      subtext: 'Dotknij ekranu, aby rozpocząć zamawianie',
      branding: {
        primaryColor: getComputedStyle(document.documentElement).getPropertyValue('--color-primary'),
        secondaryColor: getComputedStyle(document.documentElement).getPropertyValue('--color-secondary'),
        logo: null,
        backgroundImage: null
      }
    };
  }

  getDefaultSpecials() {
    return [
      {
        name: "Risotto al Tartufo Nero",
        price: 24.95,
        currency: "$",
        description: "Creamy arborio rice with seasonal wild mushrooms and freshly shaved black truffle"
      },
      {
        name: "Branzino alla Griglia",
        price: 32.95,
        currency: "$",
        description: "Grilled Mediterranean sea bass with lemon herb sauce and seasonal vegetables"
      }
    ];
  }

  render() {
    const specialsHtml = this.specialDishes.map((special) => {
      const formattedPrice = this.formatPrice(special);
      const imageSrc = special.image || special.images?.[0] || featuredDishPlaceholder;
      return `
      <div class="special-card" style="background-image:url('${imageSrc}')">
        <div class="special-card-overlay"></div>
        <div class="special-card-content">
          <h4 class="special-dish">${special.name}</h4>
          <p class="special-price">${formattedPrice}</p>
        </div>
      </div>
    `;
    }).join('');

    const backgroundImage = this.config.branding?.backgroundImage || '';
    const backgroundOpacity = this.config.branding?.backgroundImageOpacity || 100;
    const logoSrc = this.config.branding?.logo || logoLarge;
    const logoAlt = this.config.branding?.logoSettings?.alt || `${this.config.name || 'Restaurant'} logo`;
    const logoWidth = this.config.branding?.logoSettings?.width || null;
    const logoHeight = this.config.branding?.logoSettings?.height || null;
    const restaurantName = this.config.name || 'Restaurant';
    const tagline = this.config.tagline || 'Witamy w naszej restauracji';
    const subtext = this.config.subtext || 'Dotknij ekranu, aby rozpocząć zamawianie';

    return `
      <div class="welcome-container">
        <img src="${backgroundImage}"
             alt="Restaurant background"
             class="background-image"
             style="opacity: ${backgroundOpacity / 100};"
             onerror="this.src='${restaurantBg}'">
        <div class="overlay"></div>

        <div class="content">
          <div class="logo-container">
            <img src="${logoSrc}"
                 alt="${logoAlt}"
                 class="logo"
                 ${logoWidth && logoHeight ? `
                   width="${logoWidth}"
                   height="${logoHeight}"
                 ` : ''}
                 onerror="this.style.display='none'; document.querySelector('.restaurant-name').style.marginTop='0';">
            <h1 class="restaurant-name">${restaurantName}</h1>
          </div>

          <p class="tagline">${tagline}</p>
          <p class="subtext">${subtext}</p>

          <div class="buttons-container">
            <button class="view-menu-btn" id="view-menu-btn">
              Zobacz Menu <i class="fas fa-arrow-right"></i>
            </button>
          </div>

          <div class="specials-section">
            <h3 class="specials-title">
              ${this.translate('welcome.featuredTitle', 'Polecane przez nas')}
            </h3>
            <div class="specials-container">
              ${specialsHtml}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  formatPrice(special) {
    const amount = typeof special.price === 'number' ? special.price : Number(special.price ?? 0);
    const currencyCode = special.currency || this.config?.settings?.currency || 'PLN';

    const currencySymbols = {
      'PLN': 'zł',
      'EUR': '€',
      'USD': '$',
      'GBP': '£'
    };

    const symbol = special.currencySymbol || currencySymbols[currencyCode] || currencyCode;

    return `${amount.toFixed(2)} ${symbol}`;
  }

  bindEvents(onViewMenu, onAbout, onTapAnywhere) {
    const viewMenuBtn = document.getElementById('view-menu-btn');
    const welcomeContainer = document.querySelector('.welcome-container');

    if (viewMenuBtn) {
      viewMenuBtn.addEventListener('click', onViewMenu);
    }

    if (welcomeContainer) {
      welcomeContainer.addEventListener('click', (e) => {
        if (e.target.closest('#view-menu-btn')) {
          return;
        }
        onTapAnywhere();
      });
    }

    this.initializeCarousel();
  }

  initializeCarousel() {
    const container = document.querySelector('.specials-container');
    if (!container) return;

    const cards = container.querySelectorAll('.special-card');
    if (cards.length <= 3) return;

    let currentIndex = 0;
    let intervalId = null;
    const cardWidth = 480;
    const gap = 19.2;
    const scrollDistance = cardWidth + gap;
    const autoScrollInterval = 4000;

    const scrollToIndex = (index, smooth = true) => {
      const scrollPosition = index * scrollDistance;
      container.scrollTo({
        left: scrollPosition,
        behavior: smooth ? 'smooth' : 'auto'
      });
    };

    const autoScroll = () => {
      currentIndex++;

      if (currentIndex >= cards.length) {
        currentIndex = 0;
        setTimeout(() => {
          scrollToIndex(0, false);
        }, 400);
      } else {
        scrollToIndex(currentIndex, true);
      }
    };

    const startCarousel = () => {
      if (!intervalId) {
        intervalId = setInterval(autoScroll, autoScrollInterval);
      }
    };

    const stopCarousel = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    startCarousel();

    container.addEventListener('mouseenter', stopCarousel);

    container.addEventListener('mouseleave', startCarousel);

    let scrollTimeout;
    container.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollLeft = container.scrollLeft;
        currentIndex = Math.round(scrollLeft / scrollDistance);
      }, 100);
    });

    this.carouselIntervalId = intervalId;
    this.stopCarousel = stopCarousel;
  }

  show() {
    let welcomeElement = document.querySelector('.welcome-container');

    if (!welcomeElement) {
      const app = document.getElementById('app');
      const existingWelcome = app.querySelector('.welcome-container');
      if (existingWelcome) {
        existingWelcome.remove();
      }

      const welcomeHTML = this.render();
      const tableBar = document.querySelector('.table-number-bar');
      if (tableBar) {
        tableBar.insertAdjacentHTML('afterend', welcomeHTML);
      } else {
        app.insertAdjacentHTML('afterbegin', welcomeHTML);
      }
      welcomeElement = document.querySelector('.welcome-container');

      this.bindEvents(
        () => window.app.showMenuView(),
        () => console.log('About clicked'),
        () => window.app.showMenuView()
      );
    }

    if (welcomeElement) {
      const menuContainers = document.querySelectorAll('.main-container');
      menuContainers.forEach(container => {
        container.style.display = 'none';
        container.style.visibility = 'hidden';
      });

      welcomeElement.style.display = 'flex';
      welcomeElement.style.visibility = 'visible';
      welcomeElement.style.opacity = '0';
      setTimeout(() => {
        welcomeElement.style.opacity = '1';
        welcomeElement.style.transition = 'opacity 0.5s ease';
      }, 100);
    }
  }

  hide() {
    if (this.stopCarousel) {
      this.stopCarousel();
    }

    const welcomeElement = document.querySelector('.welcome-container');
    if (welcomeElement) {
      welcomeElement.style.opacity = '0';
      setTimeout(() => {
        welcomeElement.style.display = 'none';
      }, 500);
    }
  }

  translate(key, fallback) {
    try {
      const translator = window.app?.i18n;
      if (translator && typeof translator.t === 'function') {
        const value = translator.t(key);
        return value === key ? fallback : value;
      }
    } catch (error) {
      console.warn('[WelcomeScreen] Translation error:', error);
    }
    return fallback;
  }
}
